// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers. The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { Draft } from "immer";

// Define the type for Google Analytics handler configuration
export type xGoogleAnalyticsHandlerType = {
    trackGoogleAnalytics?: boolean;
    // Basic Events
    checkoutFlowEvents?: boolean;
    addToCartEvent?: boolean;
    addToCartEventName?: string;
    purchaseEvent?: boolean;
    purchaseEventName?: string;
    removeFromCartLineEvent?: boolean;
    removeFromCartLineEventName?: string;
    pdpViewEvent?: boolean;
    pdpViewEventName?: string;
    productImpressionEvent?: boolean;
    productImpressionEventName?: string;
    selectItemEvent?: boolean;
    selectItemEventName?: string;
    selectPromotionEvent?: boolean;
    selectPromotionEventName?: string;
    searchEvent?: boolean;
    searchEventName?: string;
    viewCartEvent?: boolean;
    viewCartEventName?: string;
    // Settings
    includeProductBrand?: boolean;
    includeProductCategory?: boolean;
    // Flags
    shouldTrackPayment?: boolean;
    shouldTrackBeginOnePageCheckout?: boolean;
    shouldTrackNoStockSearch?: boolean;
    affiliation?: string;
    authenticationState?: string;
    currentPage?: string;
    // Custom Events
    loginEvent?: boolean;
    loginEventName?: string;
    setDasboardAsHomepageEvent?: boolean;
    setDasboardAsHomepageEventName?: string;
    returnRequestEvent?: boolean;
    returnRequestEventName?: string;
    // Ecommerce Events
    refundEvent: boolean;
    refundEventName: string;
    addToWishListEvent: boolean;
    addToWishListEventName: string;
    beginCheckoutEvent: boolean;
    beginCheckoutEventName: string;
    addShippingInfoEvent: boolean;
    addShippingInfoEventName: string;
    addPaymentInfoEvent: boolean;
    addPaymentInfoEventName: string;
    applyPromotionCodeEvent: boolean;
    applyPromotionCodeEventName: string;
    // Custom West Events
    registrationEvent: boolean;
    registrationEventName: string;
    contactUsClickEvent: boolean;
    contactUsClickEventName: string;
    contactUsSubmitEvent: boolean;
    contactUsSubmitEventName: string;
    newsletterSubscribeEvent: boolean;
    newsletterSubscribeEventName: string;
    hpBannerClickEvent: boolean;
    hpBannerClickEventName: string;
    hpTopRightBanner1ClickEvent: boolean;
    hpTopRightBanner1ClickEventName: string;
    hpTopRightBanner2ClickEvent: boolean;
    hpTopRightBanner2ClickEventName: string;
    hpShopByCategoryEvent: boolean;
    hpShopByCategoryEventName: string;
    hpTopSellersEvent: boolean;
    hpTopSellersEventName: string;
    featuredBrandsEvent: boolean;
    featuredBrandsEventName: string;
    hpFooterWidgetEvent: boolean;
    hpFooterWidgetEventName: string;
    footerLinksClickEvent: boolean;
    footerLinksClickEventName: string;
    navBarClickEvent: boolean;
    navBarClickEventName: string;
    productCategoryClickEvent: boolean;
    productCategoryClickEventName: string;
    headerToolBarEvent: boolean;
    headerToolBarEventName: string;
    needHelpClickEvent: boolean;
    needHelpClickEventName: string;
    brandsPageLinkClickEvent: boolean;
    brandsPageLinkClickEventName: string;
    socialButtonsClickEvent: boolean;
    socialButtonsClickEventName: string;
    myAccountFlyoutEvent: boolean;
    myAccountFlyoutEventName: string;
    myAccountNavPageLinksEvent: boolean;
    pdpShareEvent: boolean;
    pdpShareEventName: string;
    pdpPrintEvent: boolean;
    pdpPrintEventName: string;
    pdpDocumentClickEvent: boolean;
    pdpDocumentClickEventName: string;
    pdpBannerClickEvent: boolean;
    pdpBannerClickEventName: string;
    fileDownloadEvent: boolean;
    fileDownloadEventName: string;
    uploadBulkOrderEvent: boolean;
    uploadBulkOrderEventName: string;
    quickBulkOrderEvent: boolean;
    quickBulkOrderEventName: string;
    updateZipCodeEvent: boolean;
    updateZipCodeEventName: string;
    hpRecentOrdersEvent: boolean;
    hpRecentOrdersEventName: string;
    hpRecentSavedCartsEvent: boolean;
    hpRecentSavedCartsEventName: string;
    compareProductsEvent: boolean;
    compareProductsEventName: string;
    autoCompleteSearchEvent: boolean;
    autoCompleteSearchEventName: string;
};

// Define the state type for Google Analytics
export type xGoogleAnalyticsStateType = Readonly<xGoogleAnalyticsHandlerType>;

// Initialize the default state for Google Analytics
const initialXGoogleAnalytics: xGoogleAnalyticsStateType = {
    trackGoogleAnalytics: false,
    // Basic Events
    checkoutFlowEvents: false,
    addToCartEvent: false,
    addToCartEventName: "add_to_cart",
    purchaseEvent: false,
    purchaseEventName: "purchase",
    removeFromCartLineEvent: false,
    removeFromCartLineEventName: "remove_from_cart",
    pdpViewEvent: false,
    pdpViewEventName: "view_item",
    productImpressionEvent: false,
    productImpressionEventName: "view_item_list",
    selectItemEvent: false,
    selectItemEventName: "select_item",
    selectPromotionEvent: false,
    selectPromotionEventName: "",
    searchEvent: false,
    searchEventName: "search",
    viewCartEvent: false,
    viewCartEventName: "view_cart",
    // Settings
    includeProductBrand: false,
    includeProductCategory: false,
    // Flags
    shouldTrackPayment: false,
    shouldTrackBeginOnePageCheckout: false,
    shouldTrackNoStockSearch: false,
    affiliation: "",
    authenticationState: "",
    currentPage: "",
    // Custom Events
    loginEvent: false,
    loginEventName: "login",
    setDasboardAsHomepageEvent: false,
    setDasboardAsHomepageEventName: "make_dash_hmpg",
    returnRequestEvent: false,
    returnRequestEventName: "return_request",
    // Ecommerce Events
    refundEvent: false,
    refundEventName: "refund",
    addToWishListEvent: false,
    addToWishListEventName: "add_to_wishlist",
    beginCheckoutEvent: false,
    beginCheckoutEventName: "begin_checkout",
    addShippingInfoEvent: false,
    addShippingInfoEventName: "add_shipping_info",
    addPaymentInfoEvent: false,
    addPaymentInfoEventName: "add_payment_info",
    applyPromotionCodeEvent: false,
    applyPromotionCodeEventName: "promo_code",
    // Custom West Events
    registrationEvent: false,
    registrationEventName: "registration",
    contactUsClickEvent: false,
    contactUsClickEventName: "contactus_click",
    contactUsSubmitEvent: false,
    contactUsSubmitEventName: "contactus_submit",
    newsletterSubscribeEvent: false,
    newsletterSubscribeEventName: "newsletter_subscribe",
    hpBannerClickEvent: false,
    hpBannerClickEventName: "hp_banner_click",
    hpTopRightBanner1ClickEvent: false,
    hpTopRightBanner1ClickEventName: "hp_topright_banner_1",
    hpTopRightBanner2ClickEvent: false,
    hpTopRightBanner2ClickEventName: "hp_topright_banner_2",
    hpShopByCategoryEvent: false,
    hpShopByCategoryEventName: "hp_shop_by_category",
    hpTopSellersEvent: false,
    hpTopSellersEventName: "hp_top_sellers_sale",
    featuredBrandsEvent: false,
    featuredBrandsEventName: "featured_brands",
    hpFooterWidgetEvent: false,
    hpFooterWidgetEventName: "hp_footer_widget",
    footerLinksClickEvent: false,
    footerLinksClickEventName: "footer_clicks",
    navBarClickEvent: false,
    navBarClickEventName: "nav_bar",
    productCategoryClickEvent: false,
    productCategoryClickEventName: "product_category_click",
    headerToolBarEvent: false,
    headerToolBarEventName: "tool_bar",
    needHelpClickEvent: false,
    needHelpClickEventName: "need_help_flyout",
    brandsPageLinkClickEvent: false,
    brandsPageLinkClickEventName: "brands_page_links",
    socialButtonsClickEvent: false,
    socialButtonsClickEventName: "social_button_click",
    myAccountFlyoutEvent: false,
    myAccountFlyoutEventName: "myacct_flyout",
    // this flag is for all my account pages Link in the side navigation
    myAccountNavPageLinksEvent: false,
    pdpShareEvent: false,
    pdpShareEventName: "pdp_share",
    pdpPrintEvent: false,
    pdpPrintEventName: "pdp_print",
    pdpDocumentClickEvent: false,
    pdpDocumentClickEventName: "pdp_doc_click",
    pdpBannerClickEvent: false,
    pdpBannerClickEventName: "pdp_banner_click",
    fileDownloadEvent: false,
    fileDownloadEventName: "file_download",
    uploadBulkOrderEvent: false,
    uploadBulkOrderEventName: "upload_bulk_order",
    quickBulkOrderEvent: false,
    quickBulkOrderEventName: "quick_bulk_order",
    updateZipCodeEvent: false,
    updateZipCodeEventName: "update_zip_code",
    hpRecentOrdersEvent: false,
    hpRecentOrdersEventName: "hp_recent_orders_click",
    hpRecentSavedCartsEvent: false,
    hpRecentSavedCartsEventName: "hp_recent_saved_carts",
    compareProductsEvent: false,
    compareProductsEventName: "compare_products",
    autoCompleteSearchEvent: false,
    autoCompleteSearchEventName: "autocomplete_search",
} as xGoogleAnalyticsStateType;

// Define the reducer to handle Google Analytics actions
const reducer = {
    // Handle updating Google Analytics configuration
    "xGoogleAnalytics/update": (
        draft: Draft<xGoogleAnalyticsStateType>,
        action: { trackGoogleAnalytics: boolean; data: xGoogleAnalyticsHandlerType },
    ) => {
        draft.trackGoogleAnalytics = action.trackGoogleAnalytics;
        // Basic Events
        draft.checkoutFlowEvents = action.data.checkoutFlowEvents ?? false;
        draft.addToCartEvent = action.data.addToCartEvent ?? false;
        draft.addToCartEventName = action.data.addToCartEventName ?? "add_to_cart";
        draft.purchaseEvent = action.data.purchaseEvent ?? false;
        draft.purchaseEventName = action.data.purchaseEventName ?? "purchase";
        draft.removeFromCartLineEvent = action.data.removeFromCartLineEvent ?? false;
        draft.removeFromCartLineEventName = action.data.removeFromCartLineEventName ?? "remove_from_cart";
        draft.pdpViewEvent = action.data.pdpViewEvent ?? false;
        draft.pdpViewEventName = action.data.pdpViewEventName ?? "view_item";
        draft.productImpressionEvent = action.data.productImpressionEvent ?? false;
        draft.productImpressionEventName = action.data.productImpressionEventName ?? "view_item_list";
        draft.selectItemEvent = action.data.selectItemEvent ?? false;
        draft.selectItemEventName = action.data.selectItemEventName ?? "select_item";
        draft.selectPromotionEvent = action.data.selectPromotionEvent ?? false;
        draft.selectPromotionEventName = action.data.selectPromotionEventName ?? "selectPromotionEventName";
        draft.searchEvent = action.data.searchEvent ?? false;
        draft.searchEventName = action.data.searchEventName ?? "search";
        draft.viewCartEvent = action.data.viewCartEvent ?? false;
        draft.viewCartEventName = action.data.viewCartEventName ?? "view_cart";
        // Settings
        draft.includeProductBrand = action.data.includeProductBrand ?? false;
        draft.includeProductCategory = action.data.includeProductCategory ?? false;
        // Custom Events
        draft.loginEvent = action.data.loginEvent ?? false;
        draft.loginEventName = action.data.loginEventName ?? "login";
        draft.setDasboardAsHomepageEvent = action.data.setDasboardAsHomepageEvent ?? false;
        draft.setDasboardAsHomepageEventName = action.data.setDasboardAsHomepageEventName ?? "make_dash_hmpg";
        draft.returnRequestEvent = action.data.returnRequestEvent ?? false;
        draft.returnRequestEventName = action.data.returnRequestEventName ?? "view_product_list";
        // Ecommerce Events
        draft.refundEvent = action.data.refundEvent ?? false;
        draft.refundEventName = action.data.refundEventName ?? "refund";
        draft.addToWishListEvent = action.data.addToWishListEvent ?? false;
        draft.addToWishListEventName = action.data.addToWishListEventName ?? "add_to_wishlist";
        draft.beginCheckoutEvent = action.data.beginCheckoutEvent ?? false;
        draft.beginCheckoutEventName = action.data.beginCheckoutEventName ?? "begin_checkout";
        draft.addShippingInfoEvent = action.data.addShippingInfoEvent ?? false;
        draft.addShippingInfoEventName = action.data.addShippingInfoEventName ?? "add_shipping_info";
        draft.addPaymentInfoEvent = action.data.addPaymentInfoEvent ?? false;
        draft.addPaymentInfoEventName = action.data.addPaymentInfoEventName ?? "add_payment_info";
        draft.applyPromotionCodeEvent = action.data.applyPromotionCodeEvent ?? false;
        draft.applyPromotionCodeEventName = action.data.applyPromotionCodeEventName ?? "promo_code";
        // Custom West Events
        draft.registrationEvent = action.data.registrationEvent ?? false;
        draft.registrationEventName = action.data.registrationEventName ?? "registration";
        draft.contactUsClickEvent = action.data.contactUsClickEvent ?? false;
        draft.contactUsClickEventName = action.data.contactUsClickEventName ?? "contactus_click";
        draft.contactUsSubmitEvent = action.data.contactUsSubmitEvent ?? false;
        draft.contactUsSubmitEventName = action.data.contactUsSubmitEventName ?? "contactus_submit";
        draft.newsletterSubscribeEvent = action.data.newsletterSubscribeEvent ?? false;
        draft.newsletterSubscribeEventName = action.data.newsletterSubscribeEventName ?? "newsletter_subscribe";
        draft.hpBannerClickEvent = action.data.hpBannerClickEvent ?? false;
        draft.hpBannerClickEventName = action.data.hpBannerClickEventName ?? "hp_banner_click";
        draft.hpTopRightBanner1ClickEvent = action.data.hpTopRightBanner1ClickEvent ?? false;
        draft.hpTopRightBanner1ClickEventName = action.data.hpTopRightBanner1ClickEventName ?? "hp_topright_banner_1";
        draft.hpTopRightBanner2ClickEvent = action.data.hpTopRightBanner2ClickEvent ?? false;
        draft.hpTopRightBanner2ClickEventName = action.data.hpTopRightBanner2ClickEventName ?? "hp_topright_banner_2";
        draft.hpShopByCategoryEvent = action.data.hpShopByCategoryEvent ?? false;
        draft.hpShopByCategoryEventName = action.data.hpShopByCategoryEventName ?? "hp_shop_by_category";
        draft.hpTopSellersEvent = action.data.hpTopSellersEvent ?? false;
        draft.hpTopSellersEventName = action.data.hpTopSellersEventName ?? "hp_top_sellers_sale";
        draft.featuredBrandsEvent = action.data.featuredBrandsEvent ?? false;
        draft.featuredBrandsEventName = action.data.featuredBrandsEventName ?? "featured_brands";
        draft.hpFooterWidgetEvent = action.data.hpFooterWidgetEvent ?? false;
        draft.hpFooterWidgetEventName = action.data.hpFooterWidgetEventName ?? "hp_footer_widget";
        draft.footerLinksClickEvent = action.data.footerLinksClickEvent ?? false;
        draft.footerLinksClickEventName = action.data.footerLinksClickEventName ?? "footer_clicks";
        draft.navBarClickEvent = action.data.navBarClickEvent ?? false;
        draft.navBarClickEventName = action.data.navBarClickEventName ?? "nav_bar";
        draft.productCategoryClickEvent = action.data.productCategoryClickEvent ?? false;
        draft.productCategoryClickEventName = action.data.productCategoryClickEventName ?? "product_category_click";
        draft.headerToolBarEvent = action.data.headerToolBarEvent ?? false;
        draft.headerToolBarEventName = action.data.headerToolBarEventName ?? "tool_bar";
        draft.needHelpClickEvent = action.data.needHelpClickEvent ?? false;
        draft.needHelpClickEventName = action.data.needHelpClickEventName ?? "need_help_flyout";
        draft.brandsPageLinkClickEvent = action.data.brandsPageLinkClickEvent ?? false;
        draft.brandsPageLinkClickEventName = action.data.brandsPageLinkClickEventName ?? "brands_page_links";
        draft.socialButtonsClickEvent = action.data.socialButtonsClickEvent ?? false;
        draft.socialButtonsClickEventName = action.data.socialButtonsClickEventName ?? "social_button_click";
        draft.myAccountNavPageLinksEvent = action.data.myAccountNavPageLinksEvent ?? false;
        draft.myAccountFlyoutEvent = action.data.myAccountFlyoutEvent ?? false;
        draft.myAccountFlyoutEventName = action.data.myAccountFlyoutEventName ?? "myacct_flyout";
        draft.pdpShareEvent = action.data.pdpShareEvent ?? false;
        draft.pdpShareEventName = action.data.pdpShareEventName ?? "pdp_share";
        draft.pdpPrintEvent = action.data.pdpPrintEvent ?? false;
        draft.pdpPrintEventName = action.data.pdpPrintEventName ?? "pdp_print";
        draft.pdpDocumentClickEvent = action.data.pdpDocumentClickEvent ?? false;
        draft.pdpDocumentClickEventName = action.data.pdpDocumentClickEventName ?? "pdp_doc_click";
        draft.pdpBannerClickEvent = action.data.pdpBannerClickEvent ?? false;
        draft.pdpBannerClickEventName = action.data.pdpBannerClickEventName ?? "pdp_banner_click";
        draft.fileDownloadEvent = action.data.fileDownloadEvent ?? false;
        draft.fileDownloadEventName = action.data.fileDownloadEventName ?? "file_download";
        draft.uploadBulkOrderEvent = action.data.uploadBulkOrderEvent ?? false;
        draft.uploadBulkOrderEventName = action.data.uploadBulkOrderEventName ?? "upload_bulk_order";
        draft.quickBulkOrderEvent = action.data.quickBulkOrderEvent ?? false;
        draft.quickBulkOrderEventName = action.data.quickBulkOrderEventName ?? "quick_bulk_order";
        draft.updateZipCodeEvent = action.data.updateZipCodeEvent ?? false;
        draft.updateZipCodeEventName = action.data.updateZipCodeEventName ?? "update_zip_code";
        draft.hpRecentOrdersEvent = action.data.hpRecentOrdersEvent ?? false;
        draft.hpRecentOrdersEventName = action.data.hpRecentOrdersEventName ?? "hp_recent_orders_click";
        draft.hpRecentSavedCartsEvent = action.data.hpRecentSavedCartsEvent ?? false;
        draft.hpRecentSavedCartsEventName = action.data.hpRecentSavedCartsEventName ?? "hp_recent_saved_carts";
        draft.compareProductsEvent = action.data.compareProductsEvent ?? false;
        draft.compareProductsEventName = action.data.compareProductsEventName ?? "compare_products";
        draft.autoCompleteSearchEvent = action.data.autoCompleteSearchEvent ?? false;
        draft.autoCompleteSearchEventName = action.data.autoCompleteSearchEventName ?? "autocomplete_search";
    },
    // Handle setting the payment tracking flag
    "xGoogleAnalytics/setShouldTrackPayment": (
        draft: Draft<xGoogleAnalyticsStateType>,
        action: { shouldTrackPayment: boolean },
    ) => {
        draft.shouldTrackPayment = action.shouldTrackPayment;
    },
    // Handle setting the payment tracking flag
    "xGoogleAnalytics/setShouldTrackBeginOnePageCheckout": (
        draft: Draft<xGoogleAnalyticsStateType>,
        action: { shouldTrackBeginOnePageCheckout: boolean },
    ) => {
        draft.shouldTrackBeginOnePageCheckout = action.shouldTrackBeginOnePageCheckout;
    },
    // Handle setting the affiliation
    "xGoogleAnalytics/setAffiliation": (draft: Draft<xGoogleAnalyticsStateType>, action: { affiliation: string }) => {
        draft.affiliation = action.affiliation;
    },
    // Handle setting the authentication state
    "xGoogleAnalytics/setAuthenticationState": (
        draft: Draft<xGoogleAnalyticsStateType>,
        action: { authenticationState: string },
    ) => {
        draft.authenticationState = action.authenticationState;
    },
    // Handle setting the authentication state
    "xGoogleAnalytics/setCurrentPage": (draft: Draft<xGoogleAnalyticsStateType>, action: { currentPage: string }) => {
        draft.currentPage = action.currentPage;
    },
};

// Create the Google Analytics reducer using Immer for immutability
const XGoogleAnalytics = createTypedReducerWithImmer(initialXGoogleAnalytics, reducer);

// Add the Google Analytics reducer to the application's reducers
(reducers as any).xGoogleAnalytics = XGoogleAnalytics;

// Define the application state including Google Analytics state
export type xGoogleAnalyticsState = {
    xGoogleAnalytics: xGoogleAnalyticsStateType;
} & ApplicationState;

// Define the type for Google Analytics reducers
type xGoogleAnalyticsReducers = {
    [key: string]: typeof XGoogleAnalytics;
};

// Define the type for Google Analytics actions
export type xGoogleAnalyticsActions =
    | Parameters<xGoogleAnalyticsReducers[keyof xGoogleAnalyticsReducers]>[1]
    | AnyAction;
